import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { functions } from "../../services/firebase";

const resetUserPassword = httpsCallable(functions, "resetUserPassword");

function getResetPasswordError(error) {
  const code = String(error?.code || "");

  if (code.includes("not-found")) return "User was not found.";
  if (code.includes("invalid-argument")) return error.message || "Check the reset details.";
  if (code.includes("permission-denied")) return "You do not have permission to reset passwords.";
  if (code.includes("unauthenticated")) return "Please log in again.";

  return error?.message || "Password reset failed. Please try again.";
}

export default function ResetPassword() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const cleanIdentifier = identifier.trim();

    if (!cleanIdentifier) {
      setError("Enter the user's email or UID.");
      return;
    }

    if (password.length < 6) {
      setError("Password should be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);

    try {
      const result = await resetUserPassword({
        identifier: cleanIdentifier,
        password,
      });

      const target = result.data?.email || result.data?.uid || cleanIdentifier;
      setSuccess(`Password reset for ${target}.`);
      setIdentifier("");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(getResetPasswordError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto w-full max-w-2xl space-y-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg sm:p-8">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Reset password
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Set a new temporary password for any existing user.
              </p>
            </div>

            <Link
              to="/signup"
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 no-underline transition hover:bg-gray-50"
            >
              Create account
            </Link>
          </div>

          {error && (
            <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          {success && (
            <p className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {success}
            </p>
          )}

          <form onSubmit={handleResetPassword} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                User email or UID
              </span>
              <input
                type="text"
                autoComplete="off"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:ring-emerald-400"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                New temporary password
              </span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:ring-emerald-400"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Confirm password
              </span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:ring-emerald-400"
              />
            </label>

            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-gray-900 py-2.5 text-sm font-medium text-white transition hover:bg-black disabled:bg-gray-400"
            >
              {saving ? "Resetting..." : "Reset password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
